import styled from "@emotion/styled";

import { Box } from "@mui/material";
import { green } from "@mui/material/colors";
import { Theme } from "@mui/material/styles";

// Base styled components that replace common classes from styles.ts
export const StyledRoot = styled.div`
  display: flex;
`;

export const StyledContent = styled.div`
  flex-grow: 1;
  height: 100vh;
  overflow: auto;
`;

export const StyledContainer = styled(Box)(({ theme }: { theme: Theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

// Spacing utilities
export const SpaceSearch = styled(Box)(({ theme }: { theme: Theme }) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
  "& > *": {
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },
  [theme.breakpoints.down("xl")]: {
    "& > *": {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      marginLeft: theme.spacing(0.5),
      marginRight: theme.spacing(0.5),
    },
  },
}));

export const SpaceSearchNoMargin = styled(Box)(
  ({ theme }: { theme: Theme }) => ({
    "& > *": {
      marginLeft: theme.spacing(0.5),
      marginRight: theme.spacing(0.5),
    },
    [theme.breakpoints.down("xl")]: {
      "& > *": {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        marginLeft: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
      },
    },
  }),
);

// Text input components
export const StyledTextInput = styled(Box)(({ theme }: { theme: Theme }) => ({
  [theme.breakpoints.up("sm")]: {
    minWidth: 150,
  },
  [theme.breakpoints.up("md")]: {
    minWidth: 200,
  },
  [theme.breakpoints.up("lg")]: {
    minWidth: 250,
  },
}));

export const StyledTextInputLong = styled(Box)(
  ({ theme }: { theme: Theme }) => ({
    [theme.breakpoints.up("sm")]: {
      minWidth: 200,
    },
    [theme.breakpoints.up("md")]: {
      minWidth: 300,
    },
    [theme.breakpoints.up("lg")]: {
      minWidth: 350,
    },
  }),
);

// Color variants
export const AccentGreenBackground = styled(Box)`
  background-color: ${green[400]};
`;

export const PurpleRain500 = styled(Box)`
  background-color: #7a7dff;
  color: #ffffff;
`;

export const PurpleRain = styled(Box)`
  background-color: #6559ff;
  color: #ffffff;
`;

export const BlueMoon = styled(Box)`
  background-color: #00b1f2;
  color: #ffffff;
`;

export const GreenDay = styled(Box)`
  background-color: #0aad69;
  color: #ffffff;
`;

export const YellowSubmarine = styled(Box)`
  background-color: #ffa900;
  color: #ffffff;
`;

export const RedWine = styled(Box)`
  background-color: #ea004f;
  color: #ffffff;
`;

export const Neutral = styled(Box)`
  background-color: #7a869a;
  color: #ffffff;
`;

// Link styles
export const StyledLink = styled.a`
  text-decoration: none;
  color: inherit;
`;

export const StyledVisitedLink = styled.a`
  text-decoration: none;
  color: inherit;
  &:visited {
    text-decoration: none;
    color: inherit;
  }
`;

export const StyledColoredLink = styled.a<{ theme?: Theme }>`
  text-decoration: none;
  color: ${({ theme }) => theme?.palette?.primary?.main || "#1976d2"};
  &:visited {
    text-decoration: none;
    color: ${({ theme }) => theme?.palette?.primary?.main || "#1976d2"};
  }
`;

// Table styles
export const StyledTableCell = styled(Box)(({ theme }: { theme: Theme }) => ({
  borderColor: theme.palette.divider,
  borderStyle: "solid",
  paddingBottom: "1.5px",
  paddingTop: "1.5px",
  paddingRight: "10px",
}));

export const StyledLeftCell = styled(StyledTableCell)`
  border-width: 0px 1px 1px 0px;
  font-weight: bold;
  width: 30%;
`;

export const StyledRightCell = styled(StyledTableCell)`
  border-width: 0px 0px 1px 0px;
  width: 70%;
`;

// Utility styles
export const Clickable = styled(Box)`
  &:hover {
    cursor: pointer;
  }
`;

export const Hide = styled(Box)`
  visibility: hidden;
  display: none;
`;

// Form controls
export const StyledFormControl = styled(Box)`
  min-width: 240px;
`;

export const StyledSelector = styled(Box)(({ theme }: { theme: Theme }) => ({
  minWidth: 330,
  marginBottom: theme.spacing(2),
}));

// Feature table container
export const FeatureTableContainer = styled(Box)(
  ({ theme }: { theme: Theme }) => ({
    marginBottom: theme.spacing(2),
    "& .MuiTableCell-root": {
      padding: "16px 24px",
    },
  }),
);

export const FeatureTabContainer = styled(Box)(
  ({ theme }: { theme: Theme }) => ({
    padding: theme.spacing(3),
  }),
);

// Button containers
export const SaveButtonContainer = styled(Box)(
  ({ theme }: { theme: Theme }) => ({
    marginTop: theme.spacing(2),
    paddingRight: theme.spacing(2),
  }),
);

export const SaveButton = styled(Box)(({ theme }: { theme: Theme }) => ({
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
}));
